import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

/**
 * AcidBaseContext — estado global del popup de análisis ácido-base.
 *
 * Motivo: el AcidBaseAnalysisDialog se renderizaba DENTRO de ExamWidgets, que
 * vive dentro del Dialog del visualizador (viewer). Un Dialog MUI anidado dentro
 * de otro Dialog queda atrapado en el stacking del padre y aparece POR DEBAJO
 * del viewer, sin importar el z-index (el z-index solo gana en dev; en el build
 * de prod el modal anidado queda atrás). El popup de cultivos NO tiene este
 * problema porque se monta a nivel App (fuera del viewer).
 *
 * Solución: elevar el estado acá y renderizar el Dialog a nivel App (ver
 * App.js), igual que el popup de cultivos. El botón "Análisis A-B" en
 * ExamWidgets llama `openAcidBase(groups)` con un snapshot de los grupos.
 */

const AcidBaseContext = createContext(null);

export function AcidBaseProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [groups, setGroups] = useState([]);

  const openAcidBase = useCallback((g) => {
    setGroups(Array.isArray(g) ? g : []);
    setOpen(true);
  }, []);
  const closeAcidBase = useCallback(() => setOpen(false), []);

  const value = useMemo(
    () => ({ open, groups, openAcidBase, closeAcidBase }),
    [open, groups, openAcidBase, closeAcidBase],
  );

  return (
    <AcidBaseContext.Provider value={value}>
      {children}
    </AcidBaseContext.Provider>
  );
}

export function useAcidBase() {
  const ctx = useContext(AcidBaseContext);
  if (!ctx) {
    return {
      open: false,
      groups: [],
      openAcidBase: () => {},
      closeAcidBase: () => {},
    };
  }
  return ctx;
}

export default AcidBaseContext;
